import {
  CoachingSessionDto,
  CoachingSessionStatus,
} from '../../../models/coach/dto/coaching-session.dto';
import { CourtDto } from '../../../models/court/dto/court.dto';
import { GetCourtCalendarOutputDto } from '../../../models/court/dto/get-court-calendar.dto';
import { EventDto, EventStatus } from '../../../models/event/dto/event.dto';
import { GameDto, GameStatus } from '../../../models/game/dto/game.dto';

/**
 * Helpers for the MYS-45 "cancel a day" flow. The window is computed
 * client-side in the operator's local timezone so the API stays
 * timezone-agnostic, and the preview mirrors the server's selection rule
 * (this court, this local day, still UPCOMING, still in the future).
 */

/** Local-day [00:00:00, 23:59:59.999] ISO window for a `YYYY-MM-DD` input. */
export function dayWindowFromDateInput(dateStr: string): {
  startTime: string;
  endTime: string;
} {
  const start = new Date(`${dateStr}T00:00:00`);
  const end = new Date(`${dateStr}T23:59:59.999`);
  return { startTime: start.toISOString(), endTime: end.toISOString() };
}

/** `YYYY-MM-DD` for a Date, in the browser's local timezone. */
export function dateInputFromDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
}

/**
 * The operator's IANA zone, sent to the batch-blockade endpoint so it builds
 * the same local day (server-side, via luxon) that `dayWindowFromDateInput`
 * built here. Falls back to UTC on the rare engine without `resolvedOptions`.
 */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Whole-day window for `POST court-temporary-blockades/batch`.
 *
 * `endHour: 24` (with `endMinute: 0`) is the endpoint's sanctioned end-of-day
 * form — see `validateTimeOfDay` in batch-create-court-blockades.ts. Using
 * 23:59 instead would leave a one-minute resellable hole at the end of a day
 * we just refunded.
 */
export const FULL_DAY_TIME_OF_DAY = {
  startHour: 0,
  startMinute: 0,
  endHour: 24,
  endMinute: 0,
} as const;

/**
 * The court's games on the given local day that the batch cancel would touch.
 * Mirrors the API's predicate exactly (see cancelCourtDayAsAdmin):
 * `status === UPCOMING && startTime >= max(now, dayStart) && startTime <= dayEnd`.
 *
 * The status check is load-bearing: the court-calendar endpoint returns
 * CANCELED games too (the calendar pages filter them out themselves), so
 * without it a day holding three games of which two were already canceled
 * previewed as "3 matchs à venir" and then reported one cancellation.
 */
export function gamesAffectedOnDay(
  games: GameDto[],
  dateStr: string,
  now: Date = new Date(),
): GameDto[] {
  const dayStart = new Date(`${dateStr}T00:00:00`).getTime();
  const dayEnd = new Date(`${dateStr}T23:59:59.999`).getTime();
  const lowerBound = Math.max(dayStart, now.getTime());
  return games.filter((game) => {
    if (game.status !== GameStatus.UPCOMING) return false;
    const t = new Date(game.startTime).getTime();
    return t >= lowerBound && t <= dayEnd;
  });
}

/**
 * Coaching sessions on the day that this flow does NOT cancel. Surfaced in the
 * preview so "annuler la journée" can't be read as covering them. Canceled
 * ones are excluded — they are already handled.
 */
export function coachingSessionsNotCanceledOnDay(
  sessions: CoachingSessionDto[],
  dateStr: string,
  now: Date = new Date(),
): CoachingSessionDto[] {
  const dayEnd = new Date(`${dateStr}T23:59:59.999`).getTime();
  const lowerBound = Math.max(
    new Date(`${dateStr}T00:00:00`).getTime(),
    now.getTime(),
  );
  return (sessions ?? []).filter((session) => {
    if (session.status === CoachingSessionStatus.CANCELED) return false;
    const t = new Date(session.startTime).getTime();
    return t >= lowerBound && t <= dayEnd;
  });
}

/** Events on the day that this flow does NOT cancel. See above. */
export function eventsNotCanceledOnDay(
  events: EventDto[],
  dateStr: string,
  now: Date = new Date(),
): EventDto[] {
  const dayEnd = new Date(`${dateStr}T23:59:59.999`).getTime();
  const lowerBound = Math.max(
    new Date(`${dateStr}T00:00:00`).getTime(),
    now.getTime(),
  );
  return (events ?? []).filter((event) => {
    if (event.status === EventStatus.CANCELLED) return false;
    const t = new Date(event.startTime).getTime();
    return t >= lowerBound && t <= dayEnd;
  });
}

export type CancelFailureReason =
  | 'already_canceled'
  | 'not_cancelable'
  | 'error';

/**
 * Map a raw per-game failure string from the batch-cancel result into a
 * friendly category the UI can translate. The strings come from
 * cancelGameAsAdmin: a status guard ("Cannot cancel a game in <status> status")
 * or a policy collision ("Game already canceled with policy ..."); anything
 * else is a transient/unknown error worth retrying.
 *
 * Per-GAME failures only. A whole-court request failure (403, network) has
 * entirely different text and gets its own generic line in the UI.
 */
export function classifyCancelFailure(error: string): CancelFailureReason {
  const e = (error || '').toLowerCase();
  if (e.includes('already canceled')) return 'already_canceled';
  if (e.includes('cannot cancel a game in')) return 'not_cancelable';
  return 'error';
}

/**
 * Distinct players across games (each gets refunded + notified).
 *
 * Deduplicates by participant id, which matters once the games span several
 * courts: one player in two games on two courts is one refunded human, and the
 * confirmation copy quotes this number.
 */
export function affectedPlayerCount(games: GameDto[]): number {
  const ids = new Set<string>();
  games.forEach((game) =>
    (game.participants ?? []).forEach((p) => {
      if (p.participant?.id) ids.add(p.participant.id);
    }),
  );
  return ids.size;
}

export type CourtDayPreviewEntry = {
  courtId: number;
  courtName: string;
  /** Undefined while that court's calendar query is still loading / errored. */
  data?: GetCourtCalendarOutputDto;
};

export type CourtDayPreview = {
  perCourt: Array<{ courtId: number; courtName: string; games: GameDto[] }>;
  /** Union across every selected court — the set the confirmation quotes. */
  allGames: GameDto[];
  gameCount: number;
  /** Distinct across ALL courts, not a sum of per-court counts. */
  playerCount: number;
  /** Not canceled by this flow — shown as a caveat. */
  coachingSessionCount: number;
  eventCount: number;
};

/** Fold per-court calendar payloads into the numbers the modal renders. */
export function buildCourtDayPreview(
  entries: CourtDayPreviewEntry[],
  dateStr: string,
  now: Date = new Date(),
): CourtDayPreview {
  const perCourt = entries.map((entry) => ({
    courtId: entry.courtId,
    courtName: entry.courtName,
    games: gamesAffectedOnDay(entry.data?.games ?? [], dateStr, now),
  }));
  const allGames = perCourt.flatMap((c) => c.games);
  const coachingSessionCount = entries.reduce(
    (sum, entry) =>
      sum +
      coachingSessionsNotCanceledOnDay(
        entry.data?.coachingSessions ?? [],
        dateStr,
        now,
      ).length,
    0,
  );
  const eventCount = entries.reduce(
    (sum, entry) =>
      sum + eventsNotCanceledOnDay(entry.data?.events ?? [], dateStr, now).length,
    0,
  );
  return {
    perCourt,
    allGames,
    gameCount: allGames.length,
    playerCount: affectedPlayerCount(allGames),
    coachingSessionCount,
    eventCount,
  };
}

export type LinkedCourtWarning = {
  courtId: number;
  courtName: string;
  /** Linked courts (parent or sub) that the operator has NOT selected. */
  linked: Array<{ id: number; name: string }>;
};

/**
 * Composite courts are separate rows: a game booked on a sub-court does not
 * belong to its parent and vice versa. Cancelling only one side leaves people
 * playing on the same physical court in the rain, so warn about any linked
 * court the operator left out rather than silently widening their selection.
 */
export function linkedCourtsMissingFromSelection(
  selectedCourtIds: number[],
  courts: CourtDto[],
): LinkedCourtWarning[] {
  const selected = new Set(selectedCourtIds);
  const warnings: LinkedCourtWarning[] = [];
  courts
    .filter((court) => selected.has(court.id))
    .forEach((court) => {
      const linked = [
        ...(court.parentCourts ?? []),
        ...(court.subCourts ?? []),
      ].filter((c) => !selected.has(c.id));
      if (linked.length > 0) {
        warnings.push({
          courtId: court.id,
          courtName: court.name,
          linked,
        });
      }
    });
  return warnings;
}
